import { useState, useRef, useEffect } from 'react';
import { MessageCircle, X, Send, Trash2, Loader2, Maximize2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { streamChat } from '@/lib/streamChat';
import MarkdownMessage from '@/components/MarkdownMessage';

export default function ChatBubble() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (open && messages.length === 0) {
      api.get('/chat/history?sessionId=bubble').then(r => {
        if (r.data?.length) setMessages(r.data);
      }).catch(() => {});
    }
  }, [open]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  useEffect(() => {
    if (open && inputRef.current) inputRef.current.focus();
  }, [open]);

  // #1: streaming odpovědí.
  const send = async () => {
    const q = input.trim();
    if (!q || loading) return;
    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: q }, { role: 'assistant', content: '' }]);
    setLoading(true);
    await streamChat({
      message: q,
      sessionId: 'bubble',
      onChunk: (chunk) => {
        setMessages(prev => {
          const copy = [...prev];
          const last = copy[copy.length - 1];
          if (last && last.role === 'assistant') {
            copy[copy.length - 1] = { ...last, content: last.content + chunk };
          }
          return copy;
        });
      },
      onError: (err) => {
        setMessages(prev => {
          const copy = [...prev];
          const last = copy[copy.length - 1];
          if (last && last.role === 'assistant' && !last.content) {
            copy[copy.length - 1] = { ...last, content: 'Omlouvám se, došlo k chybě: ' + err };
          }
          return copy;
        });
      },
    });
    setLoading(false);
  };

  const clearHistory = async () => {
    await api.delete('/chat/history?sessionId=bubble').catch(() => {});
    setMessages([]);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  return (
    <>
      {/* Floating bubble */}
      {!open && (
        <button
          data-testid="chat-bubble-btn"
          onClick={() => setOpen(true)}
          className="fixed bottom-6 right-6 z-50 w-14 h-14 rounded-full flex items-center justify-center shadow-lg transition-all hover:scale-110"
          style={{
            background: 'linear-gradient(135deg, var(--ct-accent), var(--ct-accent-hover))',
            color: '#fff',
          }}
        >
          <MessageCircle className="w-6 h-6" />
        </button>
      )}

      {/* Chat panel */}
      {open && (
        <div
          data-testid="chat-bubble-panel"
          className="fixed bottom-6 right-6 z-50 flex flex-col rounded-xl shadow-2xl overflow-hidden"
          style={{
            width: '400px',
            height: '520px',
            background: 'var(--ct-surface)',
            border: '1px solid var(--ct-border)',
          }}
        >
          {/* Header */}
          <div
            className="flex items-center justify-between px-4 py-3 flex-shrink-0"
            style={{ background: 'var(--ct-bg-secondary)', borderBottom: '1px solid var(--ct-border)' }}
          >
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
              <span className="text-sm font-semibold" style={{ fontFamily: 'Inter', color: 'var(--ct-accent)' }}>
                CalibraTech AI
              </span>
            </div>
            <div className="flex items-center gap-1">
              <button
                data-testid="chat-fullpage-btn"
                onClick={() => { setOpen(false); navigate('/chat'); }}
                className="p-1.5 rounded transition-colors hover:opacity-80"
                style={{ color: 'var(--ct-text-secondary)' }}
                title="Otevřít na celou stránku"
              >
                <Maximize2 className="w-4 h-4" />
              </button>
              <button
                data-testid="chat-clear-btn"
                onClick={clearHistory}
                className="p-1.5 rounded transition-colors hover:opacity-80"
                style={{ color: 'var(--ct-text-secondary)' }}
                title="Smazat historii"
              >
                <Trash2 className="w-4 h-4" />
              </button>
              <button
                data-testid="chat-close-btn"
                onClick={() => setOpen(false)}
                className="p-1.5 rounded transition-colors hover:opacity-80"
                style={{ color: 'var(--ct-text-secondary)' }}
                title="Zavřít"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Messages */}
          <div ref={scrollRef} className="flex-1 overflow-y-auto p-3 space-y-3">
            {messages.length === 0 && !loading && (
              <div className="text-center py-8 space-y-3">
                <MessageCircle className="w-10 h-10 mx-auto" style={{ color: 'var(--ct-text-disabled)' }} />
                <div className="text-sm" style={{ color: 'var(--ct-text-secondary)' }}>
                  Zeptejte se na cokoli z CalibraTech
                </div>
                <div className="space-y-1.5">
                  {['Kolik kalibrací čeká na schválení?', 'Které etalony brzy expirují?', 'Shrň poslední kalibrace'].map((q, i) => (
                    <button
                      key={i}
                      onClick={() => { setInput(q); }}
                      className="block w-full text-left text-xs px-3 py-2 rounded transition-colors"
                      style={{ color: 'var(--ct-accent)', background: 'rgba(var(--ct-accent-rgb), 0.08)', border: '1px solid rgba(var(--ct-accent-rgb), 0.15)' }}
                    >
                      {q}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {messages.map((msg, i) => (
              <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div
                  className={`max-w-[85%] px-3 py-2 rounded-lg text-sm ${msg.role === 'user' ? 'whitespace-pre-wrap' : ''}`}
                  style={msg.role === 'user' ? {
                    background: 'var(--ct-accent)',
                    color: '#fff',
                    borderBottomRightRadius: '4px',
                  } : {
                    background: 'var(--ct-bg)',
                    color: 'var(--ct-text)',
                    border: '1px solid var(--ct-border)',
                    borderBottomLeftRadius: '4px',
                  }}
                >
                  {msg.role === 'user' ? msg.content : <MarkdownMessage content={msg.content} />}
                </div>
              </div>
            ))}

            {loading && (
              <div className="flex justify-start">
                <div className="px-3 py-2 rounded-lg text-sm flex items-center gap-2"
                  style={{ background: 'var(--ct-bg)', border: '1px solid var(--ct-border)', color: 'var(--ct-text-secondary)' }}>
                  <Loader2 className="w-4 h-4 animate-spin" style={{ color: 'var(--ct-accent)' }} />
                  Přemýšlím...
                </div>
              </div>
            )}
          </div>

          {/* Input */}
          <div
            className="flex items-center gap-2 px-3 py-3 flex-shrink-0"
            style={{ borderTop: '1px solid var(--ct-border)', background: 'var(--ct-bg-secondary)' }}
          >
            <input
              ref={inputRef}
              data-testid="chat-bubble-input"
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Napište dotaz..."
              className="flex-1 bg-transparent text-sm outline-none"
              style={{ color: 'var(--ct-text)' }}
              disabled={loading}
            />
            <button
              data-testid="chat-bubble-send"
              onClick={send}
              disabled={!input.trim() || loading}
              className="p-2 rounded-lg transition-all disabled:opacity-30"
              style={{ background: 'var(--ct-accent)', color: '#fff' }}
            >
              <Send className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </>
  );
}
